import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Eye, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const AdminProUsers = () => {
  const navigate = useNavigate();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [discountPercentage, setDiscountPercentage] = useState(10);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    // Check admin auth
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchUsers();
  }, [navigate, activeTab]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (activeTab === 'pending') {
        const response = await axios.get(`${BACKEND_URL}/api/admin/pro-users/pending`, { headers });
        setPendingUsers(response.data);
      } else {
        const response = await axios.get(`${BACKEND_URL}/api/admin/pro-users/?status=${activeTab}`, { headers });
        setAllUsers(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Erreur lors du chargement des utilisateurs');
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(
        `${BACKEND_URL}/api/admin/pro-users/${selectedUser.id}/approve`,
        { discount_percentage: discountPercentage },
        { headers }
      );

      setSuccess(`Compte approuvé avec ${discountPercentage}% de remise !`);
      setShowModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'approbation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir rejeter cette demande ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      await axios.put(`${BACKEND_URL}/api/admin/pro-users/${userId}/reject`, {}, { headers });
      setSuccess('Compte rejeté');
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du rejet');
    }
  };

  const openApprovalModal = (user) => {
    setSelectedUser(user);
    setDiscountPercentage(10);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-celadon mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  const displayUsers = activeTab === 'pending' ? pendingUsers : allUsers;

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-2 text-celadon hover:text-celadon-light mb-4"
        >
          <ArrowLeft size={20} />
          Retour au Dashboard
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Gestion des comptes PRO</h1>
      </div>

      {/* Messages */}
      {error && (
        <div className="max-w-7xl mx-auto mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}
      {success && (
        <div className="max-w-7xl mx-auto mb-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {success}
        </div>
      )}

      {/* Tabs */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('pending')}
            className={`px-6 py-3 rounded-t-lg font-medium transition-colors ${
              activeTab === 'pending'
                ? 'bg-white text-celadon border-b-2 border-celadon'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            En attente ({pendingUsers.length})
          </button>
          <button
            onClick={() => setActiveTab('approved')}
            className={`px-6 py-3 rounded-t-lg font-medium transition-colors ${
              activeTab === 'approved'
                ? 'bg-white text-celadon border-b-2 border-celadon'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            Approuvés
          </button>
          <button
            onClick={() => setActiveTab('rejected')}
            className={`px-6 py-3 rounded-t-lg font-medium transition-colors ${
              activeTab === 'rejected'
                ? 'bg-white text-celadon border-b-2 border-celadon'
                : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
            }`}
          >
            Rejetés
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow overflow-hidden">
        {displayUsers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Aucun utilisateur dans cette catégorie
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Société</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SIRET</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Activité</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                {activeTab === 'approved' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remise</th>
                )}
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {displayUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{user.company_name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{user.prenom} {user.nom}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                    <div className="text-sm text-gray-500">{user.phone}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{user.siret}</td>
                  <td className="px-6 py-4 text-sm text-gray-900">{user.activity}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </td>
                  {activeTab === 'approved' && (
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                        {user.discount_percentage}%
                      </span>
                    </td>
                  )}
                  <td className="px-6 py-4 text-right">
                    {activeTab === 'pending' && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openApprovalModal(user)}
                          className="flex items-center gap-1 px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 transition-colors text-sm"
                        >
                          <CheckCircle size={16} />
                          Approuver
                        </button>
                        <button
                          onClick={() => handleReject(user.id)}
                          className="flex items-center gap-1 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors text-sm"
                        >
                          <XCircle size={16} />
                          Rejeter
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Approval Modal */}
      {showModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Approuver le compte PRO</h3>
            <div className="mb-6">
              <p className="text-gray-700 mb-2">Société : <strong>{selectedUser.company_name}</strong></p>
              <p className="text-gray-700 mb-4">Contact : <strong>{selectedUser.prenom} {selectedUser.nom}</strong></p>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pourcentage de remise *
              </label>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={discountPercentage}
                  onChange={(e) => setDiscountPercentage(parseFloat(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-celadon"
                />
                <span className="text-gray-700 font-medium">%</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Ce pourcentage sera appliqué sur tous les produits pour cet utilisateur.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedUser(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
                disabled={actionLoading}
              >
                Annuler
              </button>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Approbation...' : 'Approuver'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProUsers;
